import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import StatusBadge from '../components/StatusBadge'
import { api } from '../lib/api'

export default function PlaylistDetail() {
  const { id } = useParams<{ id: string }>()
  const [playlist, setPlaylist] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState<'none' | 'single' | 'bulk'>('none')
  const [singleUrl, setSingleUrl] = useState('')
  const [bulkUrls, setBulkUrls] = useState('')
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState('')

  function loadPlaylist() {
    if (!id) return
    api.getPlaylist(id).then(setPlaylist).catch(console.error).finally(() => setLoading(false))
  }

  useEffect(() => { loadPlaylist() }, [id])

  async function handleAddSingle() {
    if (!singleUrl.trim() || !id) return
    setAdding(true)
    setAddError('')
    try {
      await api.createLecture({ course_id: id, youtube_url: singleUrl.trim() })
      setSingleUrl('')
      setShowAdd('none')
      loadPlaylist()
    } catch (err: any) {
      setAddError(err.message || 'فشل الإضافة')
    } finally {
      setAdding(false)
    }
  }

  async function handleAddBulk() {
    if (!bulkUrls.trim() || !id) return
    const urls = bulkUrls.split('\n').map(u => u.trim()).filter(Boolean)
    if (urls.length === 0) return
    setAdding(true)
    setAddError('')
    try {
      await api.bulkCreateLectures({ course_id: id, urls })
      setBulkUrls('')
      setShowAdd('none')
      loadPlaylist()
    } catch (err: any) {
      setAddError(err.message || 'فشل الإضافة')
    } finally {
      setAdding(false)
    }
  }

  if (loading) return <div className="text-center text-gray-400 py-12">جاري التحميل...</div>
  if (!playlist) return <div className="text-center text-red-400 py-12">المجموعة غير موجودة</div>

  const lectures = playlist.lectures || []
  const completedCount = lectures.filter((l: any) => l.status === 'complete').length

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/playlists" className="text-gray-400 hover:text-vertex-gold transition-colors text-sm">
          المجموعات
        </Link>
        <span className="text-gray-600">›</span>
        <h2 className="text-lg font-bold text-vertex-gold line-clamp-1">{playlist.name}</h2>
      </div>

      {/* Stats bar */}
      <div className="bg-vertex-navy rounded-lg border border-vertex-surface p-4 mb-6">
        <div className="flex items-center gap-6 text-sm flex-wrap">
          <div>
            <span className="text-gray-400">المحاضرات: </span>
            <span className="text-white font-bold">{lectures.length}</span>
          </div>
          <div>
            <span className="text-gray-400">مكتملة: </span>
            <span className="text-green-400 font-bold">{completedCount}</span>
          </div>
          <div>
            <span className="text-gray-400">التقدم: </span>
            <span className="text-vertex-gold font-bold">
              {lectures.length > 0 ? Math.round((completedCount / lectures.length) * 100) : 0}%
            </span>
          </div>
          {playlist.playlist_id && (
            <a
              href={`https://www.youtube.com/playlist?list=${playlist.playlist_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-red-400 hover:text-red-300 text-xs mr-auto"
            >
              YouTube ↗
            </a>
          )}
        </div>
      </div>

      {/* Add Videos Panel */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setShowAdd(showAdd === 'single' ? 'none' : 'single')}
          className="bg-vertex-gold text-vertex-deep px-4 py-2 rounded text-sm font-bold hover:bg-vertex-gold-light transition-colors"
        >
          + فيديو واحد
        </button>
        <button
          onClick={() => setShowAdd(showAdd === 'bulk' ? 'none' : 'bulk')}
          className="bg-vertex-surface text-white px-4 py-2 rounded text-sm font-bold hover:bg-vertex-surface/80 transition-colors border border-vertex-gold/30"
        >
          + Bulk (عدة فيديوهات)
        </button>
      </div>

      {showAdd === 'single' && (
        <div className="bg-vertex-navy rounded-lg p-4 border border-vertex-gold/30 mb-4">
          <h3 className="text-vertex-gold text-sm font-bold mb-2">إضافة فيديو واحد</h3>
          <div className="flex gap-3">
            <input
              value={singleUrl}
              onChange={(e) => setSingleUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=xxxxx"
              dir="ltr"
              className="flex-1 bg-vertex-deep border border-vertex-surface rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-vertex-gold outline-none font-mono"
              onKeyDown={(e) => e.key === 'Enter' && handleAddSingle()}
            />
            <button
              onClick={handleAddSingle}
              disabled={adding || !singleUrl.trim()}
              className="bg-vertex-gold text-vertex-deep px-6 py-2 rounded text-sm font-bold hover:bg-vertex-gold-light disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              {adding ? 'جاري...' : 'أضف'}
            </button>
          </div>
          {addError && <div className="mt-2 text-xs text-red-400">{addError}</div>}
        </div>
      )}

      {showAdd === 'bulk' && (
        <div className="bg-vertex-navy rounded-lg p-4 border border-vertex-gold/30 mb-4">
          <h3 className="text-vertex-gold text-sm font-bold mb-2">إضافة عدة فيديوهات (كل رابط في سطر)</h3>
          <textarea
            value={bulkUrls}
            onChange={(e) => setBulkUrls(e.target.value)}
            placeholder={"https://www.youtube.com/watch?v=aaaa\nhttps://www.youtube.com/watch?v=bbbb\nhttps://www.youtube.com/watch?v=cccc"}
            dir="ltr"
            rows={5}
            className="w-full bg-vertex-deep border border-vertex-surface rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-vertex-gold outline-none font-mono mb-3 resize-none"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {bulkUrls.split('\n').filter(u => u.trim()).length} رابط
            </span>
            <button
              onClick={handleAddBulk}
              disabled={adding || !bulkUrls.trim()}
              className="bg-vertex-gold text-vertex-deep px-6 py-2 rounded text-sm font-bold hover:bg-vertex-gold-light disabled:opacity-50 transition-colors"
            >
              {adding ? 'جاري الإضافة...' : 'أضف الكل'}
            </button>
          </div>
          {addError && <div className="mt-2 text-xs text-red-400">{addError}</div>}
        </div>
      )}

      {/* Lectures List */}
      <div className="bg-vertex-navy rounded-lg border border-vertex-surface overflow-hidden">
        <div className="p-4 border-b border-vertex-surface">
          <h3 className="font-bold text-sm">المحاضرات ({lectures.length})</h3>
        </div>

        {lectures.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">
            لا توجد محاضرات — أضف فيديوهات من الأزرار فوق
          </div>
        ) : (
          <div className="divide-y divide-vertex-surface">
            {lectures.map((lec: any) => (
              <Link
                key={lec.id}
                to={`/lectures/${lec.id}`}
                className="flex items-center justify-between p-4 hover:bg-vertex-surface/50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-vertex-gold font-mono text-sm w-8 shrink-0">{lec.lecture_number}</span>
                  {lec.youtube_id && (
                    <div className="w-16 h-9 rounded overflow-hidden bg-vertex-deep shrink-0">
                      <img
                        src={`https://img.youtube.com/vi/${lec.youtube_id}/default.jpg`}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <span className="text-sm truncate">{lec.title_ar || lec.title_en || 'بدون عنوان'}</span>
                </div>
                <StatusBadge status={lec.status || 'raw'} />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
